// useArtifactConfigs - laedt externalized parameters pro Artefakt fuer ALLE Vorwaerts-Stufen
// in EINEM Multi-Endpoint-Aufruf (Etappe A: POST /api/transport/artifact-configs-multi).
// Cache-Key = getArtifactCacheKey(sourceId, targetInstanceId, werkKennung) - basiert auf der
// Basis-Stufe (erste Vorwaerts-Stufe), damit die Werk-Isolation wie bisher greift.
// Antwort wird als SET_CFG in den Context gehoben (entries + stageValues).

import { useCallback } from "react";
import { api } from "../../../utils/helpers";
import {
  getArtifactCacheKey,
  normalizeConfigEntry,
} from "../../../utils/transport-config-helpers";
import { useTransportV2, vorwaertsStufen } from "../context/TransportV2Context";

// Kennung-Anhang je Stufe: quellId + kennungsSuffix. Passt zur bestehenden Konvention
// (Werkskennung wird an die Artefakt-Id angehaengt: Flow_97, Flow_31 ...).
function suffixedIdFor(baseId, werkKennung) {
  if (!werkKennung) return baseId;
  return `${baseId}${werkKennung}`;
}

export function useArtifactConfigs() {
  const { state, dispatch } = useTransportV2();

  const loadFor = useCallback(async ({ sourceInstanceId, targetInstanceId, sourceArtifactId, suffixedId, werkKey, werkKennung }) => {
    const cacheKey = getArtifactCacheKey(sourceArtifactId, targetInstanceId, werkKennung);
    // Wenn schon geladen: nichts tun.
    const existing = state.cfg?.[werkKey]?.[sourceArtifactId];
    if (existing && existing.cacheKey === cacheKey) return existing.entries;

    // Alle Vorwaerts-Stufen des Space als targets. Basis = erste Stufe (typischerweise QAS
    // oder die per Space konfigurierte Ziel-Stufe). Ohne Vorwaerts-Stufen kann nichts geladen
    // werden - der alte Legacy-Fall (nur targetInstanceId als Ziel) wird als Fallback bedient.
    const stufen = vorwaertsStufen(state);
    const targets = [];
    if (stufen.length > 0) {
      for (const st of stufen) {
        // Fuer die Basis-Stufe nutzen wir suffixedId aus precheck (bereits kennung-korrekt),
        // fuer die spaeteren Stufen bauen wir denselben Suffix erneut - der Ziel-Tenant-Flow
        // heisst dort ebenfalls "Flow<kennung>". Beim Ein-Stufen-Space bleibt nur die eine Zeile.
        const targetFlowId = st.key === stufen[0].key
          ? (suffixedId || suffixedIdFor(sourceArtifactId, werkKennung))
          : suffixedIdFor(sourceArtifactId, werkKennung);
        targets.push({ stage: st.key, targetInstanceId: st.instanceId, targetFlowId });
      }
    } else {
      // Legacy-Fallback: keine Vorwaerts-Stufen ableitbar (z.B. Test ohne Scene0-State).
      targets.push({ stage: "qas", targetInstanceId, targetFlowId: suffixedId || sourceArtifactId });
    }

    try {
      const r = await api("/api/transport/artifact-configs-multi", {
        method: "POST",
        timeout: 60000,
        body: JSON.stringify({
          sourceInstanceId,
          sourceArtifactId,
          targets,
        }),
      });
      const base = Array.isArray(r?.base) ? r.base : [];
      const byStage = (r && r.byStage && typeof r.byStage === "object") ? r.byStage : {};
      // Basis-Stufe: deren Ist-Werte fliessen in normalizeConfigEntry(targetValue) und werden
      // damit zur Vorbelegung des Root-Eintrags (newValue). Spaetere Stufen wandern nach byStage.
      const basisStageKey = targets[0]?.stage || null;
      const basisValuesArr = basisStageKey ? (byStage[basisStageKey]?.values || []) : [];
      const basisValueLookup = {};
      for (const v of basisValuesArr) {
        if (v && v.ParameterKey != null) basisValueLookup[v.ParameterKey] = v.targetValue;
      }
      const entries = base
        .map((cfg) => {
          const targetValue = basisValueLookup[cfg.ParameterKey];
          // normalizeConfigEntry erwartet targetValue als Property des raw-Eintrags.
          return normalizeConfigEntry({ ...cfg, targetValue: targetValue == null ? undefined : targetValue });
        })
        .filter(Boolean);
      // stageValues: alle spaeteren Stufen (nicht Basis) - byStage-Enrichment im Reducer.
      const stageValues = {};
      for (const [stageKey, payload] of Object.entries(byStage)) {
        if (stageKey === basisStageKey) continue;
        stageValues[stageKey] = payload?.values || [];
        // Fehlertoleranz: byStage[stage].error wird nicht als Blocker gemeldet - die Stufe
        // zeigt einfach keinen Ist-Wert. Kein throw, damit ein 403/404 einer Stufe nicht die
        // ganze Konfigurationsansicht zerlegt.
      }
      dispatch({ type: "SET_CFG", payload: { werkKey, sourceId: sourceArtifactId, cacheKey, entries, stageValues } });
      return entries;
    } catch (e) {
      dispatch({ type: "SET_CFG", payload: { werkKey, sourceId: sourceArtifactId, cacheKey, entries: [] } });
      throw e;
    }
    // Bewusst schmale Deps: state.cfg (Cache-Check) + state.space (Stufenableitung).
    // Ein umfassenderes [state] wuerde loadFor bei jedem Reducer-Change neu erstellen und
    // in Scene4 unnoetiges Nachladen anstossen.
  }, [state.cfg, state.space, dispatch]);

  return { loadFor };
}
